import { yupResolver } from '@hookform/resolvers/yup'
import HelpIcon from '@mui/icons-material/Help'
import {
  Box,
  Link,
  OutlinedInput,
  Typography
} from '@mui/material'
import { CloudPay, ErrorText, MButton } from 'components'
import { MESSAGES, ROUTES_PATH } from 'config'
import { color } from 'config/color'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { APIs, type IVerifyPayBill } from 'services'
import * as yup from 'yup'
import { FormLabel } from './LoginForm'
import { AccountNumberHelpDialog } from 'components/ui/Dialog/AccountNumberHelpDialog'


const schema = yup
  .object({
    accountId: yup.string().required('Account number is required'),
    lastName: yup.string().required('Last name is required')
  })
  .required()


interface PayFormProps {
  handleTabChange: (_e: any, tabNumber: number) => void
}

export const PayForm: React.FC<PayFormProps> = ({ handleTabChange }: PayFormProps) => {
  const [account, setAccount] = useState<string>('')
  const [cloudPayVisible, setCloudPayVisible] = useState<boolean>(false)
  const navigate = useNavigate()

  const {
    handleSubmit,
    control,
    formState: { errors, isValid }
  } = useForm<IVerifyPayBill>({
    mode: 'all',
    defaultValues: {
      accountId: '',
      lastName: ''
    },
    resolver: yupResolver(schema)
  })
  const { enqueueSnackbar } = useSnackbar()
  const [open, setOpen] = useState<boolean>(false)
  const switchToAnotherTab = (): void => {
    handleTabChange(null, 1)
  }

  const onSubmit = async (body: IVerifyPayBill): Promise<void> => {
    try {
      const response = await APIs.verifyPayBill(body)
      if (response.success === true) {
        setAccount(body.accountId)
        setCloudPayVisible(true)
      } else {
        enqueueSnackbar(MESSAGES.VERIFY_PAY_BILL_ERROR, { variant: 'error' })
      }
    } catch (error) {
      enqueueSnackbar(MESSAGES.VERIFY_PAY_BILL_ERROR, { variant: 'error' })
    }
  }


  const handleCloudPayClose = (): void => {
    setCloudPayVisible(false)
  }

  return (
    <>
      <form method="post" onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" flexDirection="column" gap={3}>
          <Box display="flex" flexDirection="column" gap={1} mb={2}>
            <Typography fontSize={24} color={color.grey[900]}>Enter your account number to proceed with your payment</Typography>
            <Typography fontSize={14} color={color.grey[700]}>If you want to log in or register into the portal, <Link sx={{ cursor: 'pointer' }} onClick={switchToAnotherTab}>click here</Link>.</Typography>
          </Box>
          <Controller
            name="accountId"
            control={control}
            render={({ field: { onChange, value, name } }): JSX.Element => (
              <Box>
                <Box display="flex" alignItems="center">
                  <FormLabel>Account Number</FormLabel>
                  <Link
                    component="button"
                    onClick={() => {
                      setOpen(true)
                    }}
                  >
                    <HelpIcon sx={{ fontSize: 16, paddingBottom: '2px' }} />
                  </Link>
                </Box>
                <OutlinedInput
                  name={name}
                  placeholder="Enter your account number"
                  onChange={onChange}
                  value={value}
                  type="text"
                  fullWidth
                />
                {(errors.accountId != null) && <ErrorText>{errors.accountId.message}</ErrorText>}
              </Box>
            )}
          />
          <Controller
            name="lastName"
            control={control}
            render={({ field: { onChange, value, name } }): JSX.Element => (
              <Box>
                <FormLabel>Last Name / Business Name</FormLabel>
                <OutlinedInput
                  name={name}
                  placeholder="Enter your last name"
                  onChange={onChange}
                  value={value}
                  type="text"
                  fullWidth
                />
                {(errors.lastName != null) && <ErrorText>{errors.lastName.message}</ErrorText>}
              </Box>
            )}
          />
          <AccountNumberHelpDialog
            open={open}
            onClose={() => { setOpen(false) }}
          />
        </Box>
        <Box my={3} display="flex" flexDirection="column" gap={2}>
          <MButton
            variant="contained"
            type="submit"
            size="large"
            rounded="true"
            disabled={!isValid}
            texttransform="none"
            fullWidth
            onSubmit={handleSubmit(onSubmit)}
            sx={{ fontSize: 16 }}
          >
            Pay your Bill
          </MButton>
          <MButton
            variant="outlined"
            type="submit"
            size="large"
            rounded="true"
            texttransform="none"
            fullWidth
            onSubmit={handleSubmit(onSubmit)}
            onClick={switchToAnotherTab}
            sx={{ fontSize: 16 }}
          >
            Login or Register
          </MButton>
          <Typography fontSize={16} mt={3} color={color.grey[700]} textAlign="center">Need your account number? <Link fontSize={16} sx={{ cursor: 'pointer' }} onClick={() => { navigate(ROUTES_PATH.ACCOUNT_LOOKUP) }}>Click here to find out</Link>.</Typography>
        </Box>
      </form >
      {cloudPayVisible && (
        <CloudPay account={account} onClose={handleCloudPayClose} />
      )}

    </>
  )
}
